// src/utils/envDefaults.js
import { getEnv } from "../config/env";

const env = getEnv();
/**
 * Utility to parse complete filter objects from environment variables
 */

// Helper to parse JSON objects from env variables with fallback
const parseEnvJSON = (envValue, fallback = {}) => {
	if (!envValue) return fallback;
	try {
		return JSON.parse(envValue);
	} catch (e) {
		console.error(
			`Failed to parse env JSON: ${envValue?.substring(0, 50)}...`,
			e,
		);
		return fallback;
	}
};

// Helper to add dynamic IDs to custom filters
const addIdsToCustomFilters = (filters) => {
	if (!Array.isArray(filters)) return [];
	return filters.map((filter, index) => ({
		...filter,
		id: Date.now() + index,
	}));
};

// ========================================
// DOMAIN FILTER DEFAULTS
// ========================================
export const getDomainFilterDefaults = () => {
	const defaults = parseEnvJSON(env.REACT_APP_DOMAIN_FILTERS_DEFAULT, {
		mediaSubtype: ["Display"],
		deviceTypes: ["phone", "tablet", "desktop", "mobile/tablet"],
		distributionChannel: ["Site"],
		videoPlacement: [],
		dateRange: 30,
		geography: [],
		language: ["English"],
		contentStyle: [],
		content_style_1: [],
		content_style_2: [],
		content_style_3: [],
		safetyTier: [],
		safety_tier_1: [],
		safety_tier_2: [],
		safety_tier_3: [],
		isDirect: false,
		publishers: [],
		selectedCountries: ["US"],
		minViewability: 0,
		minCTR: 0,
		minReach: 0,
		relevanceThreshold: 75,
		minReachThreshold: 1,
		totalReach: "25M+",
		minViewabilityThreshold: 65,
		minCTRVTR: 0,
		categories: [],
		limit: 1000,
		customFilters: [],
	});

	// Add custom filters with dynamic IDs
	const customFilters = parseEnvJSON(env.REACT_APP_DOMAIN_CUSTOM_FILTERS, [
		{ column: "cprmarketrequests", threshold: 10000 },
		{ column: "impressions", threshold: 100 },
	]);

	return {
		...defaults,
		customFilters: addIdsToCustomFilters(customFilters),
	};
};

// ========================================
// APP FILTER DEFAULTS
// ========================================
export const getAppFilterDefaults = () => {
	const defaults = parseEnvJSON(env.REACT_APP_APP_FILTERS_DEFAULT, {
		mediaSubtype: ["Video"],
		deviceTypes: ["phone", "tablet", "desktop"],
		videoPlacement: [],
		geography: ["US"],
		appStore: [],
		marketSource: [],
		marketStatus: [],
		platform: [],
		appStatus: [],
		isPaid: null,
		coppa: null,
		developers: [],
		appCreators: [],
		appNames: [],
		bundleIds: [],
		contentRating: [],
		categories: [],
		languages: [],
		features: [],
		publishers: [],
		isDirect: null,
		safetyTier: [],
		minRating: null,
		minRatingCount: 0,
		downloadRanges: [],
		appAge: [],
		lastUpdated: [],
		minViewability: 0,
		minCTR: 0,
		minReach: 0,
		uploadedBundles: {
			includeList: [],
			excludeList: [],
		},
		limit: 1000,
		customFilters: [],
	});

	// Add custom filters with dynamic IDs
	const customFilters = parseEnvJSON(env.REACT_APP_APP_CUSTOM_FILTERS, [
		{ column: "cprmarketrequests", threshold: 10000 },
		{ column: "impressions", threshold: 100 },
	]);

	return {
		...defaults,
		customFilters: addIdsToCustomFilters(customFilters),
	};
};

// ========================================
// SEGMENTS FILTER DEFAULTS
// ========================================
export const getSegmentsFilterDefaults = () => {
	return parseEnvJSON(env.REACT_APP_SEGMENTS_FILTERS_DEFAULT, {
		segmentVector: {
			originalSegmentPath: null,
			dataProvider: null,
			audienceType: [],
			categoryL1: [],
			categoryL2: [],
			attributeName: [],
			attributeValue: [],
			valueQualifier: [],
			accountName: [],
			type: [],
			subType: [],
			category: [],
			subCategory: [],
			source: [],
			providerId: [],
			isAbm: false,
			isLeaf: true,
			isNavOnly: null,
			isArchived: false,
			sellable: null,
			hasMeta: null,
			status: null,
			allowedCountries: [],
			reach: 0,
		},
		segmentMetrics: {
			reach: 0,
			requests: 0,
			soldImpressions: 0,
			avgEcpm: 0,
			uniqueUsers: 0,
			advertiserSpend: 0,
		},
		cumulativeThresholds: {
			reach_relevance: 38000000,
			uniqueUsers: 1500000000,
			weightedImpressions: 700000,
			reach_composite: 38000000,
		},
		demographicFilters: {
			ageRange: { start: "", end: "" },
			location: [],
			locationType: null,
			gender: null,
		},
		categoryFilters: [],
		sortBy: "compositeScore",
		view: "DESC",
		startIndex: 0,
		noOfResult: 250,
		noOfDays: 0,
		isAudienceRequire: false,
	});
};

// ========================================
// AUDIENCE FILTER DEFAULTS
// ========================================
export const getAudienceFilterDefaults = () => {
	return parseEnvJSON(env.REACT_APP_AUDIENCE_FILTERS_DEFAULT, {
		accountName: [],
		openAudienceId: "",
		directAudienceProvider: [],
		publisherAccountId: "",
		status: [],
		type: [],
		pricing: [],
		dataSource: [],
		region: [],
		entity: [],
		event: [],
		primaryPersona: [],
		audienceStrategy: [],
		createdWith: [],
		shareable_openx_audience: [],
		reach: 0,
		estimated_reach: 0,
		addressable_reach: 0,
		CPRMarketRequests: 0,
		Impressions: 0,
		uploadedAudienceIds: {
			includeList: [],
			excludeList: [],
		},
		sortBy: "compositeScore",
		sortDirection: "DESC",
		startIndex: 0,
		noOfResult: 200,
	});
};

// ========================================
// PACKAGE FILTER DEFAULTS
// ========================================
export const getPackageFilterDefaults = () => {
	return parseEnvJSON(env.REACT_APP_PACKAGE_FILTERS_DEFAULT, {
		dealName: [],
		audienceType: [],
		primaryVertical: [],
		adPlacement: [],
		distributionChannel: [],
		deviceTypes: [],
		country: [],
		packageCategoryL1: [],
		packageCategoryL2: [],
		packageCategoryL3: [],
		isCurated: null,
		hasMeta: null,
		excludeMfa: null,
		impressions: null,
		fills: null,
		resultLimit: 100,
	});
};

// ========================================
// EXPORT ALL DEFAULTS
// ========================================
export const getAllFilterDefaults = () => ({
	domain: getDomainFilterDefaults(),
	app: getAppFilterDefaults(),
	segments: getSegmentsFilterDefaults(),
	audience: getAudienceFilterDefaults(),
	package: getPackageFilterDefaults(),
});

// Debug helper
export const logFilterDefaults = () => {
	console.log("🔧 Current Filter Defaults:", {
		domain: getDomainFilterDefaults(),
		app: getAppFilterDefaults(),
		segments: getSegmentsFilterDefaults(),
		audience: getAudienceFilterDefaults(),
		package: getPackageFilterDefaults(),
	});
};
